import React from 'react';

import { convertArrayToCSV } from 'convert-array-to-csv';
import { EChartsInstance } from 'echarts-for-react';
import SaveAsImage from 'echarts/lib/component/toolbox/feature/SaveAsImage';
import isEqual from 'fast-deep-equal';
import FileSaver from 'file-saver';
import { flatten, flattenDepth, groupBy } from 'lodash';
import { isEmpty, merge, omit, sortedUniq, uniq } from 'lodash/fp';

import { EChartsWrapper } from '../../components';
import _ from '../../locale';
import {
    DAY_KEY,
    LOWER_KEY,
    UPPER_KEY,
    VALUE_KEY,
    YEAR_KEY,
    isTimeUnitKey,
} from '../../models/data-key';
import { styled } from '../../theme';
import echartsTheme from '../../theme/echarts-theme';
import { csvIcon, gifIcon } from '../../theme/icons';
import {
    DataFilters,
    DataGranularityKey,
    DataRecord,
    DataValueKey,
    TimeUnitKey,
} from '../../types';
import {
    getLegendHeight,
    getTitlesHeight,
} from '../../utility/echarts-helpers/get-chart-component-height';
import getSelectedTimeUnitsRange from '../../utility/echarts-helpers/get-selected-time-units-range';
import getTimelineConfig from '../../utility/echarts-helpers/get-time-slider-config';
import { formatValue } from '../../utility/filters-helpers';
import formatBarChartDataLabel from '../../utility/formatting/formatBarChartDataLabel';
import wordWrapByLength from '../../utility/word-wrap-by-length';

const ChartWrapper = styled('div')({
    display: 'flex',
});

type RenderProps = ReturnType<BarChart['getRenderProps']>;

type Props = {
    xAxisKey: DataGranularityKey;
    yAxisKey: DataValueKey;
    dataKey: DataGranularityKey;
    timeUnitKey: DataGranularityKey;
    stackByKey: DataGranularityKey;
    multipleFilterKeys: DataGranularityKey[];
    withSlider: boolean;
    onEvents: object;
    enableTimelineWithFilterKey?: TimeUnitKey;
    initialTimelineValue?: string | number;
    onTimelineChange?: (value: number | string) => void;

    mobileViewWidth: number;
    configureGrid: (props: RenderProps) => Array<object>;
    configureTitles: (props: RenderProps) => Array<object>;
    configureDataZoom: (props: RenderProps) => object;
    formatXAxisLabel: (value: number, props: RenderProps) => string;
    formatYAxisLabel: (value: number, props: RenderProps) => string;
    formatLegendLabel: (value: number, props: RenderProps) => string;
    getSeriesName?: (value: number) => number;
    renderTooltip: (params: any, props: RenderProps) => string;
    renderXAxisName: (props: RenderProps) => string;
    renderYAxisName: (props: RenderProps, idx?: number) => string;
    saveAsGIF?: {
        visible?: boolean;
        filename?: () => void;
        icon?: string;
        title?: string;
        onClick?: () => void;
    };
    saveAsImage: {
        visible?: boolean;
        enabled?: boolean;
        filename?: (props: RenderProps) => string;
        icon?: string;
        title?: string;
        onClick?: () => void;
    };
    saveAsCSV: {
        visible: boolean;
        filename?: (props: RenderProps) => string;
        icon?: string;
        title?: string;
        headers?: Array<string>;
        keys?: Array<string>;
        isLocalizedData?: boolean;
    };
    theme: object;
    forwardedRef?: any;
    legendPosition: 'right' | 'bottom' | 'none';
    xAxisValues: number[] | string[];

    isLoadingData: boolean;
    filtersValues: DataFilters;
    columns: Array<DataGranularityKey>;
    records: Array<DataRecord>;

    chartInstanceByKey?: DataGranularityKey;
    hideLegend: boolean;
    showLabels?: boolean;

    displayUncertainties?: boolean;
    onUncertaintyAvailabilityChange?: (value: boolean) => void;
    barCateroryGap?: number;
    areDataLabelsDisabled?: boolean;
    horizontalLayoutAllowedKeys?: DataKey[];
    onDataLabelsEnabledChange: (value: boolean) => void;
    getChartHeight?: ({ legendHeight }: { legendHeight: number }) => number;
    withWrapXAxisLabels?: boolean;
};

type State = {
    chartWidth: number;
    zoomStartValue: number;
    zoomEndValue: number;
    zoomStartIdx: number;
    zoomEndIdx: number;
    timelineValue: string | number;
    titlesHeight: number;
    legendHeight: number;
    timelinePlayState: boolean;

    timeUnitValues: string[] | number[];
    dataset: any; // @todo: add proper type
    datasetKeys: string[];
    chartOptions: any; // @todo: add proper type
    chartInstanceKeyValues: string[] | number[];
    groupedRecords: DataRecord[][][];

    barWidth: number;
    barValueToSizeCoefs: string;
    dataLabelRotation: number;
    selectedLegend: string[];
    isHorizontalLayout: boolean;
    xAxisValues: number[] | string[];
};

const TITLE_PADDING = 15;
const X_AXIS_LABEL_MIN_WIDTH = 120;
const X_AXIS_LABEL_MIN_FONT_SIZE = 9;
const X_AXIS_LABEL_MAX_FONT_SIZE = 14;

const CHART_GRID_CONFIG = {
    left: 65,
    right: 280,
    bottom: 80,
};

class BarChart extends React.PureComponent<Props, State> {
    static defaultProps = {
        yAxisKey: VALUE_KEY,
        timeUnitKey: YEAR_KEY,
        withSlider: false,
        multipleFilterKeys: [],
        mobileViewWidth: 767,
        configureGrid: () => {},
        configureTitles: () => ({}),
        configureDataZoom: () => {},
        formatLegendLabel: () => '',
        getSeriesName: (value) => value,
        renderXAxisName: () => '',
        renderYAxisName: () => '',
        saveAsGIF: {
            visible: false,
        },
        saveAsImage: {
            visible: false,
        },
        saveAsCSV: {
            visible: false,
        },
        onEvents: {},
        legendPosition: 'bottom',
        xAxisValues: [],
        showLabels: false,
        displayUncertainties: false,
        horizontalLayoutAllowedKeys: [],
        withWrapXAxisLabels: false,
    };

    state: State = {
        chartWidth: window.innerWidth,
        zoomStartValue: 0,
        zoomEndValue: 100,
        zoomStartIdx: 0,
        zoomEndIdx: 0,
        timelineValue: 0,
        titlesHeight: 0,
        legendHeight: 0,
        timelinePlayState: false,

        timeUnitValues: [],
        chartInstanceKeyValues: [],
        dataset: null,
        datasetKeys: [],
        chartOptions: null,
        groupedRecords: [],

        barWidth: 0,
        barValueToSizeCoefs: '',
        dataLabelRotation: 0,
        selectedLegend: [],
        isHorizontalLayout: false,
        xAxisValues: [],
    };

    theme: any;
    chartInstance = null;

    constructor(props: Props) {
        super(props);

        this.theme = merge(echartsTheme, props.theme);
    }

    componentDidMount() {
        const preparedData = this.prepareData();

        this.setState({ ...preparedData });
    }

    componentDidUpdate(
        prevProps: Readonly<Props>,
        prevState: Readonly<State>,
        snapshot?: any
    ): void {
        const { records, xAxisKey, stackByKey, multipleFilterKeys, theme, xAxisValues } =
            this.props;
        const { isHorizontalLayout } = this.state;

        this.theme = merge(echartsTheme, theme);

        let newState: {
            legendHeight?: number;
            titlesHeight?: number;
            xAxisValues?: number[] | string[];
            zoomStartIdx?: number;
            zoomEndIdx?: number;
        } = {};

        const isDataChanged = !(
            isEqual(prevProps.records, records) &&
            prevProps.xAxisKey === xAxisKey &&
            prevProps.stackByKey === stackByKey &&
            prevProps.multipleFilterKeys.toString() === multipleFilterKeys.toString()
        );

        if (isDataChanged) {
            newState = {
                ...newState,
                ...this.prepareData(),
            };
        }

        if (
            prevState.isHorizontalLayout !== isHorizontalLayout ||
            !isEqual(prevProps.xAxisValues, xAxisValues)
        ) {
            newState.xAxisValues = this.prepareXAxisValues();
            newState.zoomStartIdx = 0;
            newState.zoomEndIdx = newState.xAxisValues.length - 1;
        }

        const titlesHeight = getTitlesHeight(this.chartInstance);
        if (titlesHeight !== 0) {
            newState.titlesHeight = titlesHeight;
        }

        const legendHeight =
            this.props.legendPosition === 'bottom' ? getLegendHeight(this.chartInstance) : 0;
        if (legendHeight !== 0) {
            newState.legendHeight = legendHeight;
        }

        if (
            newState.xAxisValues ||
            newState.legendHeight ||
            newState.titlesHeight ||
            isDataChanged
        ) {
            this.setState(newState);
        }

        isDataChanged && setTimeout(() => this.handleChartRenderFinish(), 250);
    }

    getFormattedValueSize = (value: number) => {
        const formattedValue = formatBarChartDataLabel(value);
        return formattedValue
            .split('')
            .reduce(
                (previousValue, currentValue) =>
                    previousValue + (currentValue === '.' || currentValue === ',' ? 4 : 8),
                0
            );
    };

    getBarCategoryGap = (): number => {
        const { barCateroryGap } = this.props;
        const { xAxisValues } = this.state;
        if (typeof barCateroryGap !== 'undefined') return barCateroryGap;
        const barsAmount = xAxisValues.length;
        if (barsAmount > 50) {
            return 1;
        } else if (barsAmount > 20) {
            return 4;
        } else if (barsAmount > 10) {
            return 6;
        }

        return 10;
    };

    formatXAxisLabel = (id) => {
        const {
            xAxisKey,
            formatXAxisLabel: xAxisLabelGetter,
            horizontalLayoutAllowedKeys,
            withWrapXAxisLabels,
        } = this.props;

        const labelName = xAxisLabelGetter(id, this.getRenderProps());

        if (!horizontalLayoutAllowedKeys.includes(xAxisKey) && !withWrapXAxisLabels) {
            return labelName;
        }

        return this.wrapAxisLabel(labelName);
    };

    wrapAxisLabel = (value: string): string => {
        const { barWidth, isHorizontalLayout } = this.state;

        const labelWidth = isHorizontalLayout ? 150 : barWidth;
        const maxLength = Math.floor((labelWidth * 12) / 100);
        if (!maxLength) return value;

        if (value.length < maxLength) {
            return value;
        }

        return wordWrapByLength(value, maxLength);
    };

    prepareXAxisValues = () => {
        const { xAxisValues } = this.props;
        const { isHorizontalLayout } = this.state;

        return isHorizontalLayout ? [...xAxisValues].reverse() : xAxisValues;
    };

    prepareData = (): Partial<State> => {
        const { chartInstanceByKey, columns, initialTimelineValue, records } = this.props;

        const timeUnitValues = this.getTimelineOptions();
        const chartInstanceKeyIdx = columns.indexOf(chartInstanceByKey);
        const chartInstanceKeyValues = uniq(records.map((record) => record[chartInstanceKeyIdx]));

        const groupedRecords = this.splitRecordsByGranularityByTimeUnit(records).map(
            (timeUnitRecords) =>
                this.splitRecordsByGranularityKey(timeUnitRecords, chartInstanceByKey).map(
                    this.groupRecordsBySeries
                )
        );
        const dataset = this.getDatasets(groupedRecords);

        return {
            timeUnitValues,
            dataset,
            groupedRecords,
            datasetKeys: dataset.map(({ source }) => this.getRecordsKey(source)),
            chartInstanceKeyValues,
            timelineValue: initialTimelineValue,
            xAxisValues: this.prepareXAxisValues(),
        };
    };

    getRecordsKey = (value: DataRecord[]): string =>
        value.map((item: DataRecord) => item.join(',')).join('_');

    isMobileView = () => this.state.chartWidth <= this.props.mobileViewWidth;

    getXAxisConfig = () => {
        const { isHorizontalLayout, barWidth } = this.state;

        const labelWidth = Math.round(Math.max(X_AXIS_LABEL_MIN_WIDTH, barWidth));

        const fontSize =
            barWidth < X_AXIS_LABEL_MAX_FONT_SIZE
                ? Math.max(Math.floor(barWidth), X_AXIS_LABEL_MIN_FONT_SIZE)
                : X_AXIS_LABEL_MAX_FONT_SIZE;

        const interval = barWidth < 7 ? 1 : 0;

        return {
            xAxis: {
                axisLabel: {
                    interval,
                    width: labelWidth,
                    rich: {
                        p: {
                            fontSize,
                            align: isHorizontalLayout ? 'right' : 'center',
                        },
                    },
                },
            },
        };
    };

    onChartResize = (width: number) => {
        this.setState({ chartWidth: width }, () => {
            const { xAxisKey, horizontalLayoutAllowedKeys } = this.props;
            if (
                this.chartInstance?._coordSysMgr?._coordinateSystems?.length &&
                horizontalLayoutAllowedKeys.includes(xAxisKey) &&
                this.chartInstance?.setOption
            ) {
                this.chartInstance.setOption(this.getXAxisConfig());
            }
            this.handleChartRenderFinish();
        });
    };

    onChartReady = (instance: EChartsInstance) => {
        this.chartInstance = instance;
        const chartWidth = instance.getWidth();
        this.onChartResize(chartWidth);
    };

    handleTimelineChange = (params) => {
        const timelineValue = this.state.timeUnitValues[params.currentIndex];
        this.setState({ timelineValue });
        if (this.props.onTimelineChange) {
            this.props.onTimelineChange(timelineValue);
        }

        this.props.onEvents?.timelinechanged?.(params);
    };

    handleTimelinePlayChanged = ({ playState }) => {
        this.setState({ timelinePlayState: playState });
    };

    getRenderProps = () => {
        const { filtersValues, timeUnitKey } = this.props;
        const { timelineValue, zoomStartIdx, zoomEndIdx, xAxisValues } = this.state;

        return {
            ...this.state,
            isMobileView: this.isMobileView(),
            filters: filtersValues,
            timelineValue: formatValue(timeUnitKey, timelineValue, _),
            timeRange: getSelectedTimeUnitsRange(
                zoomStartIdx,
                zoomEndIdx,
                xAxisValues,
                timeUnitKey,
                _
            ),
        };
    };

    getGridPositions = () => {
        const { configureGrid, legendPosition } = this.props;
        const { chartWidth, chartInstanceKeyValues } = this.state;

        const isMultiGrid = chartInstanceKeyValues && chartInstanceKeyValues.length > 1;

        const config = { ...CHART_GRID_CONFIG };
        if (this.state.isHorizontalLayout) {
            config.left = 150;
        }
        if (legendPosition !== 'right') {
            config.right = 40;
        }

        const baseGrid = {
            ...config,
            ...configureGrid(this.getRenderProps()),
            top: this.getGridTop(),
            height: this.getGridHeight(),
        };

        const grids = [baseGrid];

        if (isMultiGrid) {
            grids[0].right = '50%';
            grids.push({
                ...baseGrid,
                right: 20,
                left: chartWidth / 2 + (baseGrid?.left || 40),
            });
        }

        return grids;
    };

    getGridTop = (): number => this.state.titlesHeight + TITLE_PADDING;

    getGridHeight = (): number => {
        const { isHorizontalLayout, xAxisValues } = this.state;
        let height = 360;

        if (isHorizontalLayout) {
            height = Math.max(xAxisValues.length * 60, height);
        }

        return height;
    };

    getGridBottom = (): number => this.getGridTop() + this.getGridHeight();

    getTitlesPositions = () => {
        const { isLoadingData, records } = this.props;

        const isDataEmpty = records.length === 0;

        const noDataTitleOverride = {
            show: isDataEmpty && !isLoadingData,
            text: _('chart_error_no_data'),
            top: '40%',
            textStyle: {
                ...this.theme.textStyle,
                fontSize: 28,
                fontWeight: 700,
            },
        };

        return [...this.props.configureTitles(this.getRenderProps()), noDataTitleOverride];
    };

    getLegendOption = (position: string, hasXAxisName: boolean) => ({
        ...this.theme.legend,
        show: !this.props.hideLegend,
        icon: 'rect',
        selected: this.state.selectedLegend,
        ...(position === 'bottom'
            ? {
                  orient: 'horizontal',
                  top:
                      this.getGridBottom() +
                      (hasXAxisName ? 25 : 0) +
                      Math.max(this.getXAxisHeight(), 110),
                  left: 10,
              }
            : {
                  orient: 'vertical',
                  top: this.getGridTop(),
                  right: 0,
              }),
    });

    getDataZoomConfig = () => {
        const { configureDataZoom, timeUnitKey } = this.props;

        return [
            {
                type: 'slider',
                show: this.props.withSlider,
                start: this.state.zoomStartValue,
                end: this.state.zoomEndValue,
                ...this.theme.dataZoom,
                ...configureDataZoom(this.getRenderProps()),
                top: this.getGridBottom() + 40,
                ...(timeUnitKey === DAY_KEY && {
                    right: 100,
                    left: 100,
                }),
            },
        ];
    };

    getTooltipOption = () =>
        this.props.renderTooltip
            ? {
                  ...this.theme.tooltip,
                  trigger: 'item',
                  formatter: (params: any) =>
                      this.props.renderTooltip(params, this.getRenderProps()),
                  position: this.isMobileView() ? [0, '50%'] : null,
              }
            : null;

    getSeries = (recordsBySeries: DataRecord[][], gridIdx: number) => {
        if (recordsBySeries.length === 0) {
            return [];
        }

        const {
            columns,
            stackByKey,
            xAxisKey,
            yAxisKey,
            showLabels,
            displayUncertainties,
            onUncertaintyAvailabilityChange,
            enableTimelineWithFilterKey,
        } = this.props;
        const { isHorizontalLayout, dataLabelRotation } = this.state;
        const barCategoryGap = this.getBarCategoryGap();

        const colors = this.theme.color;
        const colorsAmount = colors.length;

        const stackByIdx = columns.indexOf(stackByKey);
        const xAxisAmount = this.getAxisesConfigs().xAxis.length;

        const series = recordsBySeries.map((records: DataRecord[], idx: number) => ({
            type: 'bar',
            stack: gridIdx + '_' + (!!stackByKey ? records[0][stackByIdx] : 'shared_stack'),
            color: colors[idx % colorsAmount],
            encode: {
                [isHorizontalLayout ? 'y' : 'x']: columns.indexOf(xAxisKey),
                [isHorizontalLayout ? 'x' : 'y']: columns.indexOf(yAxisKey),
            },
            datasetIndex: this.getDatasetIndexForRecord(records),
            xAxisIndex: Math.min(gridIdx, xAxisAmount - 1),
            yAxisIndex: gridIdx,
            name: this.getSeriesName(records[0]),
            barCategoryGap,
            ...this.theme.bar.series,
            label: {
                ...this.theme.bar.series.label,
                show: showLabels,
                formatter: this.barLabelFormatter,
                rotate: dataLabelRotation,
            },
        }));

        const uncertaintyOverlayWidthCoef = 0.2;

        const timelineValue = this.getRenderProps()?.timelineValue;
        const isTimelineEnabled = !!timelineValue;
        let shouldNotifyUncertaintyAvailabilityChange = !isTimelineEnabled;

        if (isTimelineEnabled) {
            const enableTimelineWithFilterKeyIdx = columns.indexOf(
                enableTimelineWithFilterKey as DataKey
            );
            const recordsTimelineValue = recordsBySeries[0]?.[0]?.[enableTimelineWithFilterKeyIdx];
            const areCurrentTimelineValueRecords = timelineValue === recordsTimelineValue;
            shouldNotifyUncertaintyAvailabilityChange = areCurrentTimelineValueRecords;
        }

        const visibleChartViews = (this.chartInstance?.['_chartsViews'] || []).filter(
            (view) => view.__alive && view.__id.includes('series.bar')
        );
        const visibleChartViewNames = uniq(visibleChartViews.map((view) => view.__model.name));

        const visibleSeries = series.filter((item) => visibleChartViewNames.includes(item.name));

        const visibleStackKeys = uniq(Object.values(visibleSeries).map((series) => series.stack));
        const visibleStacksAmount = visibleStackKeys.length;

        const isUncertaintyAvailable =
            visibleSeries.length === visibleStacksAmount ||
            (visibleStacksAmount === 1 && visibleChartViewNames.length === 1);

        if (shouldNotifyUncertaintyAvailabilityChange) {
            onUncertaintyAvailabilityChange &&
                onUncertaintyAvailabilityChange(isUncertaintyAvailable);
        }

        const uncertaintySeries =
            displayUncertainties &&
            isUncertaintyAvailable &&
            columns.includes(LOWER_KEY) &&
            columns.includes(UPPER_KEY)
                ? series.map((item) => ({
                      ...omit(['encode', 'dimensions', 'stack'], item),
                      type: 'custom',
                      label: {
                          show: false,
                      },
                      itemStyle: {
                          borderWidth: 1,
                      },
                      encode: {
                          [isHorizontalLayout ? 'y' : 'x']: columns.indexOf(xAxisKey),
                          [isHorizontalLayout ? 'x' : 'y']: [
                              columns.indexOf(LOWER_KEY),
                              columns.indexOf(UPPER_KEY),
                          ],
                      },
                      renderItem: (params, api) => {
                          const { dataIndexInside } = params;

                          const xValue = api.value(
                              columns.indexOf(xAxisKey as DataKey),
                              dataIndexInside
                          );

                          const getCoord = (key: DataKey) =>
                              isHorizontalLayout
                                  ? api.coord([
                                        api.value(columns.indexOf(key), dataIndexInside),
                                        xValue,
                                    ])
                                  : api.coord([
                                        xValue,
                                        api.value(columns.indexOf(key), dataIndexInside),
                                    ]);

                          const lowPoint = getCoord(LOWER_KEY);
                          const highPoint = getCoord(UPPER_KEY);
                          const xAxisItemWidth =
                              api.size(isHorizontalLayout ? [1, 0] : [0, 1])[
                                  isHorizontalLayout ? 1 : 0
                              ] - barCategoryGap;

                          const barWidth = xAxisItemWidth / visibleStacksAmount;
                          const halfBarWidth = barWidth / 2;
                          const overlayHalfWidth = halfBarWidth * uncertaintyOverlayWidthCoef;

                          const stackIdx = visibleStackKeys.indexOf(item.stack);

                          const axisIdx = isHorizontalLayout ? 1 : 0;
                          lowPoint[axisIdx] =
                              lowPoint[axisIdx] -
                              xAxisItemWidth / 2 +
                              halfBarWidth +
                              barWidth * stackIdx;
                          highPoint[axisIdx] =
                              highPoint[axisIdx] -
                              xAxisItemWidth / 2 +
                              halfBarWidth +
                              barWidth * stackIdx;

                          const shapes = isHorizontalLayout
                              ? [
                                    {
                                        x1: highPoint[0],
                                        y1: highPoint[1] - overlayHalfWidth,
                                        x2: highPoint[0],
                                        y2: highPoint[1] + overlayHalfWidth,
                                    },
                                    {
                                        x1: highPoint[0],
                                        y1: highPoint[1],
                                        x2: lowPoint[0],
                                        y2: lowPoint[1],
                                    },
                                    {
                                        x1: lowPoint[0],
                                        y1: lowPoint[1] - overlayHalfWidth,
                                        x2: lowPoint[0],
                                        y2: lowPoint[1] + overlayHalfWidth,
                                    },
                                ]
                              : [
                                    {
                                        x1: highPoint[0] - overlayHalfWidth,
                                        y1: highPoint[1],
                                        x2: highPoint[0] + overlayHalfWidth,
                                        y2: highPoint[1],
                                    },
                                    {
                                        x1: highPoint[0],
                                        y1: highPoint[1],
                                        x2: lowPoint[0],
                                        y2: lowPoint[1],
                                    },
                                    {
                                        x1: lowPoint[0] - overlayHalfWidth,
                                        y1: lowPoint[1],
                                        x2: lowPoint[0] + overlayHalfWidth,
                                        y2: lowPoint[1],
                                    },
                                ];

                          return {
                              type: 'group',
                              children: shapes.map((shape) => ({
                                  type: 'line',
                                  shape,
                                  style: api.style({ stroke: 'grey' }),
                              })),
                          };
                      },
                      z: 100,
                  }))
                : [];

        return [...series, ...uncertaintySeries];
    };

    getToolboxOption = () => ({
        top: this.isMobileView() ? 20 : 0,
        right: 30,
        z: 10,
        ...this.theme.toolbox,
        feature: this.getToolboxFeatures(),
    });

    getToolboxFeatures = () => {
        const features = {
            saveAsImage: { show: false },
        };

        const { saveAsImage, saveAsCSV, saveAsGIF } = this.props;

        if (saveAsGIF?.visible) {
            features.mySaveAsGIF = this.getSaveAsGIFConfig();
        }

        if (saveAsImage?.visible) {
            features.mySaveAsImage = this.getSaveAsImageConfig();
        }

        if (saveAsCSV?.visible) {
            features.mySaveAsCSV = this.getSaveAsCSVConfig();
        }

        return features;
    };

    getSaveAsGIFConfig = () => {
        const { xAxisKey, saveAsGIF } = this.props;

        const {
            visible,
            filename = () => 'Chart_Animation',
            icon,
            title = _('chart_save_as_gif_button_label'),
            enabled,
            onClick,
        } = saveAsGIF;

        if (!(visible && this.getTimelineOptions()?.length > 1 && !isTimeUnitKey(xAxisKey))) {
            return { visible: false };
        }

        return {
            icon: icon || gifIcon,
            title: enabled ? title : '',
            onclick: (...params) => {
                enabled && onClick?.(...params);
            },
            ...(!enabled && {
                iconStyle: {
                    color: '#A2A3A4',
                },
                emphasis: {
                    iconStyle: {
                        color: '#A2A3A4',
                    },
                },
            }),
        };
    };

    handleChartRenderFinish = () => {
        if (!this.chartInstance) {
            return null;
        }

        let barWidth = 0;
        let isHorizontalLayout = false;
        let barValueToSizeCoefs = {};
        let maxFormattedValueSize = 0;

        const { onDataLabelsEnabledChange, xAxisKey, horizontalLayoutAllowedKeys } = this.props;

        const { _chartsViews } = this.chartInstance;
        const chartViews = (_chartsViews || []).filter(
            (view) => view?.__alive && view?.__id.includes('series.bar')
        );

        chartViews.forEach((view) => {
            barWidth = Math.max(barWidth, view?._data?._layout?.size);
        });

        isHorizontalLayout =
            horizontalLayoutAllowedKeys.includes(xAxisKey) && barWidth < X_AXIS_LABEL_MIN_WIDTH;
        let dataLabelRotation = isHorizontalLayout ? 0 : 90;

        chartViews.forEach((view) => {
            const componentIdx = view?.__model?.componentIndex;
            const data = view?._data;
            const storage = data?._store;
            const visibleItemsAmount = storage?._count;
            let visibleIndices = storage?._indices;
            let itemLayouts = data?._itemLayouts;
            const valueIdx = data?.dimensions?.findIndex(
                (dimension) => dimension.toLowerCase() === VALUE_KEY
            );
            let values = storage?._chunks?.[valueIdx];

            if (visibleIndices?.length) {
                visibleIndices = visibleIndices.slice(0, visibleItemsAmount);
                values = values.filter((item, idx) => visibleIndices.includes(idx));
            }

            if (itemLayouts?.length && values?.length) {
                const absValues = [].slice.call(values).map(Math.abs);
                const maxBarValue = Math.max(...absValues);

                maxFormattedValueSize = Math.max(
                    maxFormattedValueSize,
                    ...absValues.map(this.getFormattedValueSize)
                );

                if (!isHorizontalLayout && maxFormattedValueSize < barWidth) {
                    dataLabelRotation = 0;
                }

                const maxBarSize = Math.max(
                    ...itemLayouts.map(({ width, height }) =>
                        Math.abs(isHorizontalLayout ? width : height)
                    )
                );

                if (maxBarValue && maxBarSize) {
                    barValueToSizeCoefs[componentIdx] = maxBarValue / maxBarSize;
                }
            }
        });

        onDataLabelsEnabledChange?.(barWidth > 15);

        this.setState({
            dataLabelRotation,
            barWidth,
            barValueToSizeCoefs: Object.entries(barValueToSizeCoefs)
                .map(([key, value]) => `${key}=${value}`)
                .join('|'),
            isHorizontalLayout,
        });
    };

    // @todo: move into echarts-helpers
    getSaveAsImageConfig = () => {
        const {
            visible,
            enabled,
            filename = (renderProps?) => 'Chart_ScreenShot',
            icon,
            title = _('chart_save_as_image_button_label'),
            onClick,
        } = this.props.saveAsImage;

        if (!visible) {
            return undefined;
        }

        const config = {
            excludeComponents: ['toolbox', 'graphic'],
            pixelRatio: 2,
            title,
            name: filename(this.getRenderProps()),
            onclick: function (...params) {
                if (!enabled) {
                    return;
                }
                SaveAsImage.prototype.onclick.call(this, ...params);
                onClick && onClick();
            },
            ...(!enabled && {
                iconStyle: {
                    color: '#A2A3A4',
                },
                emphasis: {
                    iconStyle: {
                        color: '#A2A3A4',
                    },
                },
            }),
        };

        return icon ? { ...config, icon } : config;
    };

    addSaveAsImageOverlap = (): object | null => {
        const { visible, enabled } = this.props.saveAsImage;

        return visible && !enabled
            ? {
                  type: 'rect',
                  cursor: 'default',
                  z: 1000,
                  top: 20,
                  right: 30,
                  shape: {
                      width: 30,
                      height: 40,
                  },
                  style: {
                      fill: 'rgba(100,100,100,0)',
                  },
              }
            : null;
    };

    // @todo: move into echarts-helpers
    getSaveAsCSVConfig = () => {
        const {
            visible,
            filename = () => 'Chart_Data',
            icon,
            title = _('chart_save_as_csv_button_label'),
        } = this.props.saveAsCSV;

        if (!visible) {
            return undefined;
        }

        return {
            icon: icon || csvIcon,
            title,
            onclick: () => {
                const name = filename(this.getRenderProps()) + '.csv';
                const data = new Blob([this.getCSVData()], {
                    type: 'text/plain',
                });
                FileSaver.saveAs(data, name);
            },
        };
    };

    getCSVData = () => {
        const { columns, records, saveAsCSV } = this.props;

        const columnHeaders = saveAsCSV.headers;
        const columnsKeys = saveAsCSV.keys || [];

        const arrayRecords = records.map((record) => [
            ...columnsKeys.map((key) =>
                saveAsCSV.isLocalizedData
                    ? record[columns.indexOf(key)]
                    : formatValue(record[columns.indexOf(key)], key, _)
            ),
        ]);

        return convertArrayToCSV(arrayRecords, { header: columnHeaders });
    };

    onDataZoomChange = ({ start, end }, echartsInstance: EChartsInstance) => {
        const { xAxisValues } = this.state;
        let zoomStartIdx = 0;
        let zoomEndIdx = Math.max(0, xAxisValues.length - 1);

        const dataZoomOption = echartsInstance?.getOption()?.dataZoom?.[0];
        if (dataZoomOption) {
            zoomStartIdx = dataZoomOption.startValue;
            zoomEndIdx = dataZoomOption.endValue;
        }

        this.setState({ zoomStartValue: start, zoomEndValue: end, zoomStartIdx, zoomEndIdx });

        this.handleChartRenderFinish();
    };

    getTimelineOptions = () => {
        const { columns, records, enableTimelineWithFilterKey } = this.props;
        const sliderIndex = columns.indexOf(enableTimelineWithFilterKey);

        return sortedUniq(records.map((record) => record[sliderIndex]).sort());
    };

    getChartOptions = (groupedData, dataset) => {
        if (!(groupedData && groupedData.length && dataset && dataset.length)) {
            return [];
        }

        return groupedData.map((timeUnitGroup) => ({
            series: flatten(
                timeUnitGroup.map((chartInstanceGroup, idx) =>
                    this.getSeries(chartInstanceGroup, idx)
                )
            ),
        }));
    };

    getAxisesConfigs = (): object => {
        const { formatYAxisLabel, renderXAxisName, renderYAxisName, records } = this.props;

        const { chartInstanceKeyValues, isHorizontalLayout, xAxisValues } = this.state;

        const { xAxis: xAxisTheme, yAxis: yAxisTheme } = this.theme;

        if (!(records && records.length)) {
            // 'No Data Available' case
            return { xAxis: [xAxisTheme], yAxis: yAxisTheme };
        }

        return {
            [isHorizontalLayout ? 'yAxis' : 'xAxis']: (chartInstanceKeyValues || []).map(
                (key, idx) => ({
                    axisTick: { show: false },
                    splitLine: { show: false },
                    ...xAxisTheme,
                    axisLabel: {
                        ...xAxisTheme.axisLabel,
                        formatter: this.formatXAxisLabel,
                    },
                    name: this.isTimelineOrDatazoomVisible()
                        ? ''
                        : renderXAxisName(this.getRenderProps()),
                    data: xAxisValues.map((i) => i.toString()),
                    nameGap: isHorizontalLayout ? 130 : 50,
                    gridIndex: idx,
                })
            ),
            [isHorizontalLayout ? 'xAxis' : 'yAxis']: chartInstanceKeyValues.map((key, idx) => ({
                ...yAxisTheme,
                axisTick: { show: false },
                axisLabel: {
                    ...yAxisTheme.axisLabel,
                    formatter: (value: number) => formatYAxisLabel(value, this.getRenderProps()),
                },
                name: renderYAxisName(this.getRenderProps(), idx),
                gridIndex: idx,
            })),
        };
    };

    groupRecordsByGranularityKey = (
        records: DataRecord[],
        granularityKey: DataGranularityKey
    ): Record<string | number, DataRecord[]> | null => {
        if (!records.length) {
            return null;
        }

        const { columns } = this.props;
        const granularityKeyIdx = columns.indexOf(granularityKey);

        return groupBy(records, (record) => record[granularityKeyIdx]);
    };

    splitRecordsByGranularityKey = (
        records: DataRecord[],
        granularityKey: DataGranularityKey
    ): DataRecord[][] => {
        if (!records.length) {
            return [];
        }
        return Object.values(this.groupRecordsByGranularityKey(records, granularityKey));
    };

    splitRecordsByGranularityByTimeUnit = (records: DataRecord[]): DataRecord[][] => {
        if (!records.length) {
            return [];
        }
        const { enableTimelineWithFilterKey, timeUnitKey } = this.props;
        if (!enableTimelineWithFilterKey) {
            return [records];
        }

        return Object.values(this.groupRecordsByGranularityKey(records, timeUnitKey));
    };

    groupRecordsBySeries = (records: DataRecord[]): DataRecord[] => {
        if (!records.length) {
            return [];
        }

        const { columns, dataKey, multipleFilterKeys, stackByKey } = this.props;
        const groupByKeys = uniq([...multipleFilterKeys, dataKey, stackByKey]);
        const groupByKeysIdxs = groupByKeys.map((key) => columns.indexOf(key));

        return Object.values(
            groupBy(records, (record) => groupByKeysIdxs.map((idx) => record[idx]).join('_'))
        );
    };

    getDatasets = (data) =>
        flattenDepth(data, 2).map((records) => ({
            source: this.stringifyXAxisValues(records),
            dimensions: this.props.columns,
        }));

    // @todo: measure runtime is required
    stringifyXAxisValues = (records) => {
        const { columns, xAxisKey } = this.props;
        const xAxisKeyIdx = columns.indexOf(xAxisKey);

        return records.map((record) =>
            record.map((value, idx) => (idx === xAxisKeyIdx ? value.toString() : value))
        );
    };

    getSeriesName = (record: DataRecord): string => {
        const { columns, dataKey, stackByKey, getSeriesName } = this.props;
        const dataKeyIdx = columns.indexOf(dataKey);
        const dataKeyValue = record[dataKeyIdx];

        const stackByIdx = columns.indexOf(stackByKey);
        const stackByValue = record[stackByIdx];

        return getSeriesName(dataKeyValue, this.getRenderProps(), stackByValue, record);
    };

    getDatasetIndexForRecord = (records: DataRecord[]): number => {
        const recordsKey = this.getRecordsKey(records);
        return this.state.datasetKeys.findIndex((key) => key === recordsKey);
    };

    isTimelineOrDatazoomVisible = (): boolean => {
        const { enableTimelineWithFilterKey, withSlider } = this.props;

        return (
            (!!enableTimelineWithFilterKey && this.state.timeUnitValues.length > 2) || withSlider
        );
    };

    getGraphicConfig = (hasXAxisName: boolean) => {
        const { timeUnitKey } = this.props;

        const label = this.isTimelineOrDatazoomVisible()
            ? _(`chart_axis_label_${timeUnitKey}`)
            : '';

        return {
            elements: [
                {
                    type: 'text',
                    left: 'center',
                    style: {
                        text: label,
                        y:
                            this.getGridBottom() +
                            this.getXAxisHeight() +
                            55 +
                            (hasXAxisName ? 20 : 0),
                        font: '500 1.4rem "Avenir", "Arial", sans-serif',
                        fill: '#333333',
                    },
                },
                this.addSaveAsImageOverlap(),
            ],
        };
    };

    getXAxisHeight = () => {
        const { xAxisValues } = this.state;

        if (!xAxisValues || !xAxisValues.length) return 10;

        return (
            10 +
            Math.max(
                ...xAxisValues.map((i) => {
                    const formattedLabel = this.formatXAxisLabel(i.toString());
                    return formattedLabel.split('\n').length;
                })
            ) *
                14
        );
    };

    barLabelFormatter = (params) => {
        const { areDataLabelsDisabled } = this.props;
        if (areDataLabelsDisabled) {
            return '';
        }

        const { componentIndex, data, dimensionNames } = params;
        const valueIdx = dimensionNames.indexOf(VALUE_KEY);
        const value = data[valueIdx];

        const { barValueToSizeCoefs, barWidth, isHorizontalLayout, dataLabelRotation } = this.state;
        let coefs = {};
        barValueToSizeCoefs.split('|').forEach((str) => {
            const [key, value] = str.split('=');
            coefs[key] = parseFloat(value);
        });
        const barSize = value / coefs[componentIndex];

        const formattedValueWidth = this.getFormattedValueSize(value);

        let isEnoughSpace = formattedValueWidth < barSize;

        if (!isHorizontalLayout && dataLabelRotation === 0) {
            isEnoughSpace = barSize > 15 && formattedValueWidth < barWidth;
        }

        return isEnoughSpace ? formatBarChartDataLabel(value) : '';
    };

    handleLegendSelectChange = ({ selected }) => {
        this.setState({ selectedLegend: selected });
    };

    getChartEvents = (): Record<string, Function> => ({
        ...this.props.onEvents,
        finished: this.handleChartRenderFinish,
        legendselectchanged: this.handleLegendSelectChange,
        datazoom: this.onDataZoomChange,
        timelinechanged: this.handleTimelineChange,
        timelineplaychanged: this.handleTimelinePlayChanged,
    });

    getEChartsOption = (xAxis, yAxis) => {
        const { legendPosition, enableTimelineWithFilterKey } = this.props;
        const {
            timelineValue,
            chartWidth,
            timelinePlayState,
            dataset,
            groupedRecords,
            timeUnitValues,
        } = this.state;

        const options = this.getChartOptions(groupedRecords, dataset);

        const hasXAxisName = !!xAxis?.[0]?.name;

        const xAxisHeight = this.getXAxisHeight();

        return {
            baseOption: {
                timeline: {
                    ...getTimelineConfig(
                        enableTimelineWithFilterKey,
                        timelineValue,
                        timeUnitValues,
                        this.theme,
                        chartWidth,
                        _,
                        {
                            top: this.getGridBottom() + xAxisHeight + (hasXAxisName ? 30 : 0),
                            padding: [10, 50, 5, 50],
                        }
                    ),
                    autoPlay: timelinePlayState,
                },
                grid: this.getGridPositions(),
                title: this.getTitlesPositions(),
                legend: this.getLegendOption(legendPosition, hasXAxisName),
                tooltip: this.getTooltipOption(),
                toolbox: this.getToolboxOption(),
                dataZoom: options && options.length ? this.getDataZoomConfig() : null,
                graphic: this.getGraphicConfig(hasXAxisName),
                xAxis,
                yAxis,
                dataset,
            },
            options,
        };
    };

    getChartHeight = () => {
        const { legendHeight } = this.state;

        return (
            this.props.getChartHeight?.({ legendHeight }) ||
            this.getGridBottom() + this.getXAxisHeight() + 110 + legendHeight
        );
    };

    render() {
        const { forwardedRef, isLoadingData } = this.props;
        const { xAxis, yAxis } = this.getAxisesConfigs();
        if (isEmpty(xAxis) || isEmpty(yAxis)) {
            return null;
        }

        const option = this.getEChartsOption(xAxis, yAxis);

        return (
            // chart-wrapper is required for resize in PDF export
            <ChartWrapper id="chart-wrapper">
                <EChartsWrapper
                    ref={forwardedRef}
                    option={option}
                    style={{
                        width: '100%',
                        height: this.getChartHeight(),
                    }}
                    onEvents={this.getChartEvents()}
                    onChartReady={this.onChartReady}
                    onChartResize={this.onChartResize}
                    showLoading={isLoadingData}
                    loadingOption={this.theme.loading}
                    theme={this.theme}
                />
            </ChartWrapper>
        );
    }
}

export default React.forwardRef((props, ref) => <BarChart {...props} ref={ref} />);
